import React, { useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Users, Briefcase, MessagesSquare, LogOut } from 'lucide-react';
import { useLiqAuth } from './auth';
import { api } from './api';
import { preloadPhotos } from './components/photo';

const NAV = [
  { to: '/liq/clients', label: 'Client', icon: Users, testid: 'nav-client' },
  { to: '/liq/matters', label: 'Matter', icon: Briefcase, testid: 'nav-matter' },
  { to: '/liq/agents', label: 'Agent', icon: MessagesSquare, testid: 'nav-agent' },
];

export default function LiqLayout() {
  const { user, logout, loading } = useLiqAuth();
  const nav = useNavigate();

  // Preload every agent photo the moment user lands anywhere in /liq/*.
  // Subsequent navigation to AgentHub / MatterDetail / ClientHub reuses the cache.
  useEffect(() => {
    if (!user) return;
    api.get('/employees').then((r) => {
      const ids = (r.data.items || []).filter((e) => e.entityKind === 'agent').map((e) => e.id);
      preloadPhotos(ids);
    }).catch(() => {});
  }, [user?.id]);

  if (loading) return null;
  if (!user) {
    if (typeof window !== 'undefined' && !window.location.pathname.endsWith('/login')) {
      window.location.href = '/liq/login';
    }
    return null;
  }

  return (
    <div className="min-h-screen text-slate-100 font-sans relative overflow-x-hidden" data-testid="liq-layout"
         style={{ background: 'radial-gradient(1200px 600px at 15% -10%, rgba(201,169,97,0.08), transparent), radial-gradient(900px 500px at 100% 0%, rgba(16,185,129,0.05), transparent), #06080f' }}>
      {/* subtle grain */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]"
           style={{ backgroundImage: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22><filter id=%22n%22><feTurbulence baseFrequency=%220.9%22 stitchTiles=%22stitch%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22 opacity=%220.5%22/></svg>')" }} />

      <header className="relative border-b border-white/[0.06] backdrop-blur-xl bg-white/[0.02]">
        <div className="max-w-[1400px] mx-auto px-8 h-16 flex items-center justify-between">
          {/* Wordmark */}
          <div className="flex items-center gap-4">
            <div className="flex flex-col leading-none">
              <span className="text-[22px] tracking-[0.18em] font-serif" style={{ color: '#e6c76d', textShadow: '0 0 24px rgba(230,199,109,0.15)' }}>
                LEGAL <span className="font-normal">IQ</span>
              </span>
              <span className="mt-1 text-[9px] tracking-[0.32em] uppercase text-amber-200/40">Intelligence · Advantage</span>
            </div>
          </div>

          {/* Center pill nav */}
          <nav className="hidden md:flex items-center gap-1 p-1 rounded-full border border-white/[0.06] bg-white/[0.02] backdrop-blur">
            {NAV.map((n) => {
              const Icon = n.icon;
              return (
                <NavLink key={n.to} to={n.to}
                  className={({ isActive }) =>
                    `relative flex items-center gap-2 px-5 py-2 rounded-full text-[13px] transition-all duration-300 ${
                      isActive
                        ? 'text-amber-100 bg-gradient-to-b from-amber-500/20 to-amber-600/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]'
                        : 'text-slate-400 hover:text-slate-100 hover:bg-white/[0.03]'
                    }`
                  }
                  data-testid={n.testid}>
                  <Icon size={14} strokeWidth={1.8} />
                  <span className="tracking-wide">{n.label}</span>
                </NavLink>
              );
            })}
          </nav>

          {/* User */}
          <div className="flex items-center gap-3 text-sm">
            <div className="text-right hidden sm:block">
              <div className="text-slate-100 text-[13px] flex items-center gap-2 justify-end">
                {user.fullName}
                <span className="inline-block text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border bg-emerald-500/10 text-emerald-300 border-emerald-500/40">Admin</span>
              </div>
              <div className="text-slate-500 text-[10px] tracking-wider uppercase">{user.role}</div>
            </div>
            <button
              onClick={() => { logout(); nav('/liq/login'); }}
              className="group flex items-center gap-1.5 text-xs text-slate-400 hover:text-amber-200 border border-white/[0.06] hover:border-amber-500/30 rounded-full px-3 py-2 transition-all"
              data-testid="liq-logout"
              title="Log out"
            ><LogOut size={13} strokeWidth={1.8} /><span className="hidden sm:inline">Log out</span></button>
          </div>
        </div>
        {/* Mobile nav */}
        <nav className="md:hidden flex items-center justify-around border-t border-white/[0.06] px-4 py-2">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs ${
                    isActive ? 'text-amber-100 bg-amber-500/10' : 'text-slate-400'
                  }`
                }>
                <Icon size={13} strokeWidth={1.8} />{n.label}
              </NavLink>
            );
          })}
        </nav>
      </header>

      {/* workspace banner removed per user request */}

      <main className="relative max-w-[1400px] mx-auto px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
}
